import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useResultsList() {
  // Column info metadata for UI help modal
  const tableColumnInfo = [
    {
      key: "expectedScore",
      label: "Exp. Score",
      description:
        "Expected Score - Employee's target score based on level and seniority (Time in Role).",
      modifiable: false,
    },
    {
      key: "aggregatedScore",
      label: "Agg. Score",
      description:
        "Aggregated Total Score - Weighted combination of all the received Senior (80%) & Junior (20%) survey evaluations.",
      modifiable: false,
    },
    {
      key: "aggregatedSelfEval",
      label: "Self",
      description: "Self Evaluation Score - Employee's own self-evaluation.",
      modifiable: false,
    },
    {
      key: "aggregatedType1",
      label: "Senior Feedback",
      description:
        "Aggregated Senior Evaluation Score - Average of all Senior (Type1) survey evaluations.",
      modifiable: false,
    },
    {
      key: "aggregatedType2",
      label: "Junior Feedback",
      description:
        "Aggregated Junior Evaluation Score- Average of all Junior (Type2) survey evaluations.",
      modifiable: false,
    },
    {
      key: "proposedDecision",
      label: "Decision",
      description: "Admin's Proposed Decision for the employee. The Admin can choose between 'Neither', 'Warning', or 'Promotion'.",
      modifiable: true,
    },
    {
      key: "discussion",
      label: "Disc.",
      description: "Flag for Discussion. The Admin can indicate if discussion concerning the employee's performance/promotion is needed.",
      modifiable: true,
    },
    {
      key: "advice",
      label: "Advice",
      description: "Advice for the employee. The Admin can choose to add advice related to the employee's performance to be displayed on the employee's results page.",
      modifiable: true,
    },
    {
      key: "comments",
      label: "Comments",
      description: "Additional comments. The Admin can add any notes or comments to this section.",
      modifiable: true,
    },
  ];
  // Use toast
  const toast = useToast()

  const refResultListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: "result", sortable: true },
    // { key: 'level', label: 'Level', sortable: true },
    // { key: 'location', sortable: true },
    { key: "timeInRole", label: "Time in Role", sortable: true },
    { key: "advisor", label: "CGF", sortable: true, thClass: "nameOfTheClass" },
    { key: "expectedScore", label: "Exp. Score", sortable: true },
    { key: "aggregatedScore", label: "Agg. Score", sortable: true },
    { key: "aggregatedSelfEval", label: "Self", sortable: true },
    { key: "aggregatedType1", label: "Senior Feedback", sortable: true },
    { key: "aggregatedType2", label: "Junior Feedback", sortable: true },
    // { key: "competencyScores", label: "Flag" },
    { key: "proposedDecision", label: "Decision", sortable: true },
    { key: "discussion", label: "Disc.", sortable: true },
    { key: "advice", sortable: true },
    { key: "comments", sortable: true },
    { key: "actions" },
  ];
  const limit = ref(10)
  const totalResults = ref(0)
  const page = ref(1)
  const perPageOptions = [5, 10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('updatedAt')
  const sortOrder = ref('desc')
  const roleFilter = ref(null)
  const levelFilter = ref(null)
  const statusFilter = ref(null)
  const proposedDecisionFilter = ref(null)
  const discussionFilter = ref(null)
  const selectedCycle = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refResultListTable.value ? refResultListTable.value.localItems.length : 0
    return {
      from: limit.value * (page.value - 1) + (localItemsCount ? 1 : 0),
      to: limit.value * (page.value - 1) + localItemsCount,
      of: totalResults.value,
    }
  })

  const refetchData = () => {
    refResultListTable.value.refresh()
  }

  watch([page, limit, searchQuery, roleFilter, levelFilter, statusFilter, discussionFilter, proposedDecisionFilter], () => {
    refetchData()
  })

  watch([selectedCycle], () => {
    refetchData()

  })

  const isAdmin = computed(() => JSON.parse(localStorage.getItem('userData')).role === 'admin')

  const fetchResults = (ctx, callback) => {
    store
      .dispatch('app-result/fetchResults', {
        id: selectedCycle.value,
        q: searchQuery.value,
        limit: limit.value,
        page: page.value,
        sortBy: sortBy.value,
        sortOrder: sortOrder.value,
        role: roleFilter.value,
        level: levelFilter.value,
        proposedDecision: proposedDecisionFilter.value,
        discussion: discussionFilter.value,
        isActive: statusFilter.value === 'active' ? true : statusFilter.value === 'inactive' ? false : null,
      })
      .then(response => {
        const { results, total } = response.data
        callback(results)
        totalResults.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching results list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveResultRoleVariant = role => {
    if (role === 'result') return 'primary'
    if (role === 'admin') return 'info'
    return 'primary'
  }

  const resolveResultRoleIcon = role => {
    if (role === 'subscriber') return 'ResultIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'ResultIcon'
  }

  const resolveResultStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  const resolveResultDecisionVariant = status => {
    if (status === 'neither') return 'secondary'
    if (status === 'promotion') return 'success'
    if (status === 'warning') return 'danger'
    return 'primary'
  }

  const resolveCompetencyFlag = (competencyScores) => {
    let minScore = 100;
    for (const competency in competencyScores) {
      const scores = competencyScores[competency];
      minScore = Math.min(minScore, Math.min(scores.type1, scores.type2));
    }
    if (minScore < 1) return "red";
    if (minScore < 2) return "orange";
    if (minScore < 3) return "green";

    return 'default';
  };

  return {
    fetchResults,
    tableColumns,
    tableColumnInfo,
    limit,
    page,
    totalResults,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    sortOrder,
    refResultListTable,

    resolveResultRoleVariant,
    resolveResultRoleIcon,
    resolveResultStatusVariant,
    resolveResultDecisionVariant,
    refetchData,
    resolveCompetencyFlag,

    // Extra Filters
    roleFilter,
    levelFilter,
    proposedDecisionFilter,
    discussionFilter,

    selectedCycle,
    isAdmin,
  }
}
